import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { schedulerService } from '../../services/scheduler.service';
import { SchedulerJob, SchedulerLog } from '../../types/scheduler';
import { toastService } from '../../services/toast.service';
import { authService } from '../../services/auth.service';
import { format } from 'date-fns';

export const SchedulerManager: React.FC = () => {
  const [jobs, setJobs] = useState<SchedulerJob[]>([]);
  const [logs, setLogs] = useState<SchedulerLog[]>([]);
  const [isAutoRunEnabled, setIsAutoRunEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [processingJob, setProcessingJob] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<unknown>(null);
  const [debugError, setDebugError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsData, statusData] = await Promise.all([
        schedulerService.getJobs(),
        schedulerService.getStatus(),
      ]);
      setJobs(jobsData);
      setIsAutoRunEnabled(statusData.isAutoRunEnabled);
    } catch (error) {
      console.error('Failed to load scheduler data', error);
      toastService.error('Не вдалося завантажити дані планувальника');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const logsData = await schedulerService.getLogs(rowsPerPage, page * rowsPerPage);
      setLogs(logsData.items);
      setTotalLogs(logsData.total);
    } catch (error) {
      console.error('Failed to load logs', error);
      toastService.error('Не вдалося завантажити логи');
    } finally {
      setLogsLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleToggleAutoRun = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    try {
      const status = await schedulerService.toggleAutoRun(newValue);
      setIsAutoRunEnabled(status.isAutoRunEnabled);
      toastService.success(`Автозапуск ${newValue ? 'увімкнено' : 'вимкнено'}`);
    } catch (error) {
      console.error('Failed to toggle auto run', error);
      toastService.error('Помилка перемикання автозапуску');
      setIsAutoRunEnabled(!newValue); 
    }
  };

  const handleRunJob = async (jobName: string) => {
    setProcessingJob(jobName);
    try {
      await schedulerService.runJob(jobName);
      toastService.success(`Задача ${jobName} успішно запущена`);
      setTimeout(loadLogs, 1000);
    } catch (error) {
      console.error(`Failed to run job ${jobName}`, error);
      toastService.error(`Помилка запуску задачі ${jobName}`);
    } finally {
      setProcessingJob(null);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDebug = async () => {
    setDebugLoading(true);
    setDebugResult(null);
    setDebugError(null);
    try {
      const result = await schedulerService.debug();
      setDebugResult(result);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setDebugError(message);
      toastService.error('Помилка debug-запиту');
    } finally {
      setDebugLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString || dateString === 'N/A') return '-';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss');
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Планувальник задач (Cron)</Typography>
        <Button startIcon={<RefreshIcon />} onClick={() => { loadData(); loadLogs(); }}>
          Оновити
        </Button>
      </Box>

      {/* Status Control */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: isAutoRunEnabled ? '#e8f5e9' : '#ffebee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Глобальний статус</Typography>
            <Typography variant="body2" color="text.secondary">
              {isAutoRunEnabled 
                ? 'Планувальник активний. Задачі виконуються автоматично згідно розкладу.' 
                : 'Планувальник зупинено. Автоматичне виконання задач вимкнено.'}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isAutoRunEnabled}
                onChange={handleToggleAutoRun}
                color="success"
              />
            }
            label={isAutoRunEnabled ? "УВІМКНЕНО" : "ВИМКНЕНО"}
          />
        </Box>
      </Paper>

      {/* Jobs Table */}
      <Typography variant="h6" gutterBottom>Список задач</Typography>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Статус</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell>Опис</TableCell>
                <TableCell>Cron</TableCell>
                <TableCell>Наступний запуск</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const hasError = !!job.error;
                const isDisabled = !job.enabled;
                
                return (
                  <TableRow 
                    key={job.name} 
                    hover 
                    sx={{ 
                      bgcolor: hasError ? 'rgba(211, 47, 47, 0.05)' : isDisabled ? 'rgba(0, 0, 0, 0.02)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      {hasError ? (
                        <Tooltip title={job.error}>
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        </Tooltip>
                      ) : isDisabled ? (
                        <Tooltip title="Вимкнено">
                          <PauseCircleOutlineIcon color="action" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Активно">
                          <CheckCircleOutlineIcon color="success" fontSize="small" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      color: hasError ? 'error.main' : 'inherit'
                    }}>
                      {job.name}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={hasError ? 'error.main' : 'text.primary'}>
                        {job.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={job.cronExpression} 
                        size="small" 
                        variant="outlined" 
                        color={job.cronExpression === 'N/A' ? 'default' : 'primary'}
                        sx={{ fontFamily: 'monospace' }} 
                      />
                    </TableCell>
                    <TableCell>{formatDate(job.nextRun)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Запустити вручну">
                        <span>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleRunJob(job.name)}
                            disabled={!!processingJob || isDisabled}
                            size="small"
                          >
                            {processingJob === job.name ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">Задач не знайдено</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Debug Section */}
      <Typography variant="h6" gutterBottom>Debug</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={debugLoading ? <CircularProgress size={16} /> : <BugReportIcon />}
            onClick={handleDebug}
            disabled={debugLoading}
          >
            GET /scheduler/debug
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            Authorization: Bearer {authService.getAccessToken() ?? '—'}
          </Typography>
        </Box>
        {(debugResult !== null || debugError) && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: debugError ? 'error.main' : 'text.primary',
            }}
          >
            {debugError ?? JSON.stringify(debugResult, null, 2)}
          </Box>
        )}
      </Paper>

      {/* Logs Table */}
      <Typography variant="h6" gutterBottom>Історія виконання</Typography>
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Задача</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Початок</TableCell>
                <TableCell>Тривалість</TableCell>
                <TableCell>Повідомлення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell>
                </TableRow>
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{log.id}</TableCell>
                    <TableCell>{log.jobName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.status} 
                        color={log.status === 'success' ? 'success' : 'error'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{formatDate(log.startedAt)}</TableCell>
                    <TableCell>{log.durationMs} ms</TableCell>
                    <TableCell>
                      <Tooltip title={log.errorDetails || ''} arrow placement="top">
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {log.message}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">Логів не знайдено</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalLogs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
